import React, { useEffect, useMemo, useState, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import { AdminLayout } from "../components/AdminLayout";
import { MultiMarkerMap } from "../components/MultiMarkerMap";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import {
  Map as MapIcon, Car, IdCard, Star, Phone, Filter, Loader2, Clock, Navigation,
} from "lucide-react";

const PIN_COLORS = { green: "#22c55e", yellow: "#f59e0b", red: "#ef4444", blue: "#3b82f6" };
const LEGEND = [
  { c: "green", t: "Disponível" }, { c: "yellow", t: "Ocupado" },
  { c: "red", t: "Indisponível" }, { c: "blue", t: "Supervisor" },
];
const LANGS = ["Português", "Inglês", "Espanhol", "Crioulo", "Francês"];
const RADII = [5, 10, 20, 50];

const STATUS_LABEL = { disponivel: "Disponível", ocupado: "Ocupado", inativo: "Indisponível", supervisor: "Supervisor" };

// Popup como nó DOM real (Leaflet), para os botões terem handlers de verdade.
function buildPopupNode(p, onAssign) {
  const el = document.createElement("div");
  el.style.width = "220px";
  const photo = p.photo_path
    ? `<img src="${fileUrl(p.photo_path)}" style="width:44px;height:44px;border-radius:50%;object-fit:cover" />`
    : `<div style="width:44px;height:44px;border-radius:50%;background:#f1f5f9;display:flex;align-items:center;justify-content:center;font-weight:700;color:#94a3b8">${(p.name || "?").charAt(0)}</div>`;
  const dist = p.distance_miles != null
    ? `<div>📍 ${p.distance_miles} mi${p.eta_min != null ? ` · ~${p.eta_min} min` : ""}</div>` : "";
  const langs = (p.languages || []).length ? `<div>🗣️ ${(p.languages || []).join(", ")}</div>` : "";
  const avail = p.availability ? `<div>🕒 ${p.availability}</div>` : "";
  const rating = p.rating != null ? ` · ⭐ ${p.rating}` : "";
  el.innerHTML = `
    <div style="display:flex;gap:8px;align-items:center">
      ${photo}
      <div style="min-width:0">
        <div style="font-weight:600;font-size:13px">${p.name || ""}</div>
        <div style="font-size:11px;color:#64748b">${p.city || "—"} · ${STATUS_LABEL[p.status] || ""}</div>
      </div>
    </div>
    <div style="font-size:11px;color:#475569;margin-top:6px;line-height:1.5">
      ${dist}
      <div>🚗 ${p.has_car ? "Sim" : "Não"} · 🪪 ${p.has_license ? "Sim" : "Não"}${rating}</div>
      ${langs}${avail}
    </div>`;
  const actions = document.createElement("div");
  actions.style.cssText = "display:flex;gap:6px;margin-top:8px";
  if (p.phone) {
    const call = document.createElement("a");
    call.href = `tel:${p.phone}`;
    call.textContent = "Entrar em contato";
    call.setAttribute("data-testid", `tm-contact-${p.id}`);
    call.style.cssText = "flex:1;text-align:center;font-size:11px;font-weight:600;padding:6px;border-radius:8px;background:#ecfdf5;color:#047857;text-decoration:none";
    actions.appendChild(call);
  }
  if (p.source === "bank") {
    const btn = document.createElement("button");
    btn.textContent = "Atribuir serviço";
    btn.setAttribute("data-testid", `tm-assign-${p.id}`);
    btn.style.cssText = "flex:1;font-size:11px;font-weight:600;padding:6px;border-radius:8px;background:#eff6ff;color:#1d4ed8;border:none;cursor:pointer";
    btn.onclick = () => onAssign(p.id);
    actions.appendChild(btn);
  }
  el.appendChild(actions);
  return el;
}

export function TalentMapView() {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [matchClient, setMatchClient] = useState("");
  const [matches, setMatches] = useState(null);
  const [f, setF] = useState({ only_available: false, only_with_car: false, city: "", language: "", radius: "" });

  const setFilter = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.only_available) params.only_available = true;
      if (f.only_with_car) params.only_with_car = true;
      if (f.city) params.city = f.city;
      if (f.language) params.language = f.language;
      const res = await api.get("/talent-bank/map", { params });
      setPoints(res.data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [f.only_available, f.only_with_car, f.city, f.language]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients", { params: { limit: 500 } })
      .then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, []);

  const runMatch = async (clientId) => {
    setMatchClient(clientId);
    if (!clientId) { setMatches(null); return; }
    try {
      const res = await api.get("/talent-bank/match", {
        params: { client_id: clientId, radius_miles: f.radius || 50 },
      });
      setMatches(res.data);
      toast.success(`${res.data.matches.length} colaborador(es) próximo(s)`);
    } catch (err) {
      setMatches(null);
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const shown = matches ? matches.matches : points;

  const doAssign = useCallback(async (id) => {
    try {
      await api.post(`/talent-bank/${id}/assign`);
      toast.success("Serviço atribuído");
      load();
    } catch { toast.error("Não foi possível atribuir"); }
  }, [load]);

  const markers = useMemo(() => {
    const arr = shown.filter((p) => p.lat && p.lng).map((p) => ({
      lat: p.lat, lng: p.lng, color: PIN_COLORS[p.color] || PIN_COLORS.green,
      popupNode: buildPopupNode(p, doAssign),
    }));
    if (matches?.client) {
      arr.push({
        lat: matches.client.lat, lng: matches.client.lng, color: "#111827",
        popupHTML: `<b>Serviço:</b> ${matches.client.name}`,
      });
    }
    return arr;
  }, [shown, matches, doAssign]);

  return (
    <div className="space-y-4" data-testid="talent-map-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapIcon className="text-blue-600" /> Mapa de Colaboradores
          </h1>
          <div className="flex items-center gap-3 text-xs">
            {LEGEND.map((l) => (
              <span key={l.c} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ background: PIN_COLORS[l.c] }} /> {l.t}
              </span>
            ))}
          </div>
        </div>

        {/* Filtros + busca inteligente */}
        <div className="bg-white rounded-xl border p-3 flex flex-wrap gap-2 items-center" data-testid="tm-filters">
          <span className="text-slate-500 flex items-center gap-1 text-sm"><Filter size={15} /> Filtros:</span>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer min-h-[40px]">
            <input type="checkbox" className="w-4 h-4" data-testid="tm-only-available" checked={f.only_available} onChange={(e) => setFilter("only_available", e.target.checked)} /> Disponíveis
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer min-h-[40px]">
            <input type="checkbox" className="w-4 h-4" data-testid="tm-only-car" checked={f.only_with_car} onChange={(e) => setFilter("only_with_car", e.target.checked)} /> Com carro
          </label>
          <input className="cm-input !w-[130px] flex-none" placeholder="Cidade" data-testid="tm-city" value={f.city} onChange={(e) => setFilter("city", e.target.value)} />
          <select className="cm-input !w-[130px] flex-none" data-testid="tm-lang" value={f.language} onChange={(e) => setFilter("language", e.target.value)}>
            <option value="">Idioma</option>{LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <div className="h-6 w-px bg-slate-200 mx-1" />
          <select className="cm-input !w-[220px] flex-none" data-testid="tm-match-client" value={matchClient} onChange={(e) => runMatch(e.target.value)}>
            <option value="">🔎 Buscar p/ um serviço…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.city || ""}</option>)}
          </select>
          <select className="cm-input !w-[110px] flex-none" data-testid="tm-radius" value={f.radius}
            onChange={(e) => { setFilter("radius", e.target.value); if (matchClient) runMatch(matchClient); }}>
            <option value="">Raio</option>{RADII.map((r) => <option key={r} value={r}>{`${r} mi`}</option>)}
          </select>
          {matches && (
            <button onClick={() => runMatch("")} data-testid="tm-clear-match" className="text-xs text-blue-600 underline">Limpar busca</button>
          )}
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          {/* Mapa */}
          <div className="bg-white rounded-xl border overflow-hidden" style={{ height: 560 }} data-testid="tm-map">
            {loading && markers.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
            ) : (
              <MultiMarkerMap markers={markers} height={560} zoom={5} />
            )}
          </div>

          {/* Painel de resultados da busca inteligente */}
          <div className="bg-white rounded-xl border p-3 overflow-y-auto" style={{ height: 560 }} data-testid="tm-side">
            {matches ? (
              <>
                <div className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <Navigation size={15} className="text-blue-600" /> Próximos de {matches.client.name}
                </div>
                <div className="text-xs text-slate-400 mb-2">Ordenado por distância · quem tem carro primeiro</div>
                {matches.matches.length === 0 ? (
                  <div className="text-sm text-slate-400 py-8 text-center">Nenhum colaborador no raio.</div>
                ) : matches.matches.map((m) => (
                  <div key={`${m.source}-${m.id}`} className="flex items-center gap-2 py-2 border-b last:border-0" data-testid={`tm-match-${m.id}`}>
                    <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ background: PIN_COLORS[m.color] }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{m.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        {m.distance_miles != null && <span>{m.distance_miles} mi</span>}
                        {m.eta_min != null && <span className="flex items-center gap-0.5"><Clock size={11} /> {m.eta_min} min</span>}
                        {m.has_car && <Car size={12} className="text-indigo-500" />}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-sm text-slate-400 text-center py-10">
                <Navigation size={28} className="mx-auto mb-3 opacity-40" />
                Selecione um serviço/cliente no filtro acima para encontrar os colaboradores mais próximos, com distância e tempo estimado de chegada.
              </div>
            )}
          </div>
        </div>
      </div>
  );
}

export default function TalentMap() {
  return (
    <AdminLayout>
      <TalentMapView />
    </AdminLayout>
  );
}

function PinPopup() { return null; }
